import * as React from "react";
import { connect } from "react-redux";

import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IPersonAssociation } from "../../../types/persons/person";
import { Privilege } from "../../../types/persons/privilege";
import { Role } from "../../../types/persons/Role";
import AssociationSelector from "../../management/Association/AssociationSelector";
import PrivilegeForm from "../../management/generic/PrivilegeForm";
import RoleForm from "../../management/generic/RoleForm";

import PersonApi from "../../../data/api/personsApi";
import { IState } from "../../../data/reducers/state";
import { useTenures } from "../../../hooks/useTenures";
import { IAssociation } from "../../../types/association/Association";
import { CenteredLoader } from "../../common/loader";

// Helper component to use the tenure hook
const TenureSelect: React.FC<{
  associationId: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ associationId, value, onChange }) => {
  const { tenureOptions, defaultTenure } = useTenures(associationId);

  return (
    <Select
      value={value}
      onChange={(val) => onChange(val || defaultTenure || '')}
      data={tenureOptions}
      placeholder="Select tenure"
    />
  );
};

interface PersonAssociationModalProps {
  personId: string;
  onCancel: () => void;
  onSave: (associations: IPersonAssociation) => void;
  returnUnsaved: boolean;

  associations: IAssociation[];
  defaultValues?: Partial<IPersonAssociation>;
}

interface PersonAssociationModalState extends IPersonAssociation {
  association: IAssociation | null;
  loading: boolean;
  error?: string | null;
}

class PersonAssociationModal extends React.PureComponent<
  PersonAssociationModalProps,
  PersonAssociationModalState
> {
  constructor(props, context) {
    super(props, context);

    this.state = {
      role: Role.referee,
      privilege: Privilege.none,
      start: new Date(),
      finish: null,
      associationId: "",
      association: null,
      adminPayrate: undefined,
      tenure: undefined,

      loading: false,
    };

    const defaults = props.defaultValues;
    if (defaults) {
      const association =
        defaults.associationId &&
        props.associations.find((a) => a._id === defaults.associationId);

      this.state = {
        ...this.state,
        associationId: defaults.associationId || this.state.associationId,
        association: association || this.state.association,
        role: defaults.role ?? this.state.role,
        privilege: defaults.privilege ?? this.state.privilege,
        start: defaults.start ? new Date(defaults.start) : this.state.start,
        finish: defaults.finish ?? this.state.finish,
        adminPayrate: defaults.adminPayrate ?? this.state.adminPayrate,
        tenure: defaults.tenure ?? this.state.tenure,
      };
    }

    this.save = this.save.bind(this);
  }

  save() {
    const { personId } = this.props;
    const { associationId, role, privilege, start, adminPayrate, tenure } = this.state;

    this.setState((state) => ({ ...state, loading: true, error: null }));

    const retVal: IPersonAssociation = {
      associationId,
      privilege,
      role,
      start,
      adminPayrate,
      tenure,
    };

    if (this.props.returnUnsaved) {
      this.setState((state) => ({ ...state, loading: false }));
      return this.props.onSave(retVal);
    }

    PersonApi.associatePerson(personId, retVal)
      .then((person) => {
        console.log(person);
        this.setState((state) => ({ ...state, loading: false }));
        this.props.onSave(retVal);
      })
      .catch((er) =>
        this.setState((state) => ({
          ...state,
          error: er.message,
          loading: false,
        })),
      );
  }

  updateField(name: string, value: any) {
    this.setState((state) => ({
      ...state,
      [name]: value,
    }));
  }

  render() {
    const fields = { Association: !!this.state.associationId };
    const invalidFields = Object.keys(fields).filter((a) => !fields[a]);

    return (
      <Modal opened onClose={this.props.onCancel} title="Add Association Role" size="lg">
        <Modal.Body>
          <Stack gap="lg">
            <Stack gap="xs">
              <Text size="sm" fw={600}>Association</Text>
              <AssociationSelector
                associations={this.props.associations}
                onChange={(a) =>
                  this.setState((state) => ({
                    ...state,
                    associationId: a._id,
                    association: a,
                  }))
                }
                value={this.state.associationId}
              />
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <Stack gap="xs">
                <Text size="sm" fw={600}>Roles</Text>
                <Text size="xs" c="dimmed">
                  Choose every role this person should hold within the association.
                </Text>
                <RoleForm
                  role={this.state.role}
                  onChange={(r) => this.updateField("role", r)}
                />
              </Stack>
              <Stack gap="xs">
                <Text size="sm" fw={600}>Privileges</Text>
                <Text size="xs" c="dimmed">
                  Grant management access when required. Leave blank for standard access.
                </Text>
                <PrivilegeForm
                  privilege={this.state.privilege}
                  onChange={(p) => this.updateField("privilege", p)}
                />
              </Stack>
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <Stack gap="xs">
                <Text size="sm" fw={600}>Tenure</Text>
                <TenureSelect
                  associationId={this.state.associationId}
                  value={this.state.tenure ?? ''}
                  onChange={(value) => this.updateField("tenure", value)}
                />
              </Stack>
              <Stack gap="xs">
                <Text size="sm" fw={600}>Admin payrate (override)</Text>
                <NumberInput
                  placeholder="Admin payrate"
                  leftSection="$"
                  min={0}
                  step={0.5}
                  value={this.state.adminPayrate ?? undefined}
                  onChange={(value) =>
                    this.updateField(
                      "adminPayrate",
                      typeof value === 'number' && !Number.isNaN(value)
                        ? value
                        : undefined,
                    )
                  }
                />
              </Stack>
            </SimpleGrid>

            <Alert color="yellow" radius="md">
              Saving this role will end-date the existing association record and create a
              fresh enrolment for the selected association.
            </Alert>

            {this.state.loading && <CenteredLoader />}
          </Stack>
        </Modal.Body>
        <Group justify="flex-end" gap="sm" px="md" pb="md">
          <Button variant="outline" color="gray" onClick={this.props.onCancel}>
            Close
          </Button>
          <Tooltip
            label={invalidFields.length > 0 ? `Invalid fields: ${invalidFields.join(", ")}` : ""}
            disabled={invalidFields.length === 0}
          >
            <Button
              id="person-association-add-save"
              onClick={this.save}
              disabled={invalidFields.length > 0}
              loading={this.state.loading}
            >
              Save
            </Button>
          </Tooltip>
        </Group>
      </Modal>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    associations: state.associations.admin.map(
      (aid) => state.associations.map[aid],
    ),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    // actions: bindActionCreators({  }, dispatch)
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PersonAssociationModal);
